import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { MEMO_BODY_MAX, MEMO_TITLE_MAX, type MemoView } from "@portal/schemas";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
} from "@portal/ui";
import { Pin, PinOff, Save, Share2, Trash2 } from "lucide-react";
import { Suspense, useEffect, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import {
  createMemo,
  deleteMemo,
  markMemoRead,
  updateMemo,
  updateMemoPinned,
} from "../../api/client";
import { useInvalidate, useMe, useMemoDetail } from "../../api/queries";
import { InlineError } from "../../components/InlineError";
import { MEMO_ACCESS_LABELS } from "./memo-labels";
import { clearDraft, draftIdOfMemo, readDraft, writeDraft, type MemoDraft } from "./memo-draft";
import { LazyMarkdownEditor, LazyMarkdownView } from "../../components/lazyMarkdown";
import { MemoShareDialog } from "./MemoShareDialog";
import { useUserName } from "./ShareUserPicker";
import { Hint } from "../../components/Hint";
import { MemoIcon, MemoIconPicker } from "./MemoIcon";
import { useMentionItems } from "./mentions";

const NEW_MEMO_HINT = "Markdown で書けます。保存すると自分だけに見えるメモになります。";
const NEW_PROJECT_MEMO_HINT =
  "Markdown で書けます。保存すると、全員に見えるプロジェクトのメモになります。参加者は誰でも編集できます。";

/** 本文の入力欄(エディタの textarea)へ移り、末尾にカーソルを置く。 */
function focusBody(form: HTMLFormElement | null) {
  const body = form?.querySelector("textarea");
  if (!body) return;
  body.focus();
  body.setSelectionRange(body.value.length, body.value.length);
}

const formSchema = z.object({
  title: z.string().max(MEMO_TITLE_MAX, `タイトルは ${MEMO_TITLE_MAX} 文字までです`),
  body: z.string().max(MEMO_BODY_MAX, `本文は ${MEMO_BODY_MAX} 文字までです`),
  /** アイコン(Notion のように題名の左。2026-10-15)。未設定は空。 */
  icon: z.string().max(64),
});
type FormValues = z.infer<typeof formSchema>;

/** 画面の高さに合わせる。保存ボタンまでスクロールせずに届く高さにする。 */
const EDITOR_HEIGHT = "max(240px, calc(96vh - 400px))";

const when = new Intl.DateTimeFormat("ja-JP", {
  month: "numeric",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "Asia/Tokyo",
});

/**
 * メモの作成・編集・閲覧(大きなダイアログ。右ドロワーは使わない)。memoId が null なら新規。
 * 閲覧のみで共有されたメモは読むだけで、所有者には共有と削除の操作も出す。
 * 入力は自動で下書きとして保存し、復元は開いたときに本人が選ぶ。
 * プロジェクトのメモ(§13.6)は全員に見えるので共有の操作を出さず、参加者にはボードへのピン留めを出す。
 * ピンのメモを開いたら既読にする(行に乗せたときの先読みでは既読にしない)。
 */
export function MemoDialog({
  memoId,
  draftId,
  projectId = null,
  open,
  onOpenChange,
}: {
  memoId: string | null;
  /** 新しいメモをプロジェクトのメモとして作るなら、そのプロジェクト。 */
  projectId?: string | null;
  /** 新しいメモの下書き ID(memoId が null のとき)。既存のメモの下書きは、メモの ID から決まる。 */
  draftId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const detail = useMemoDetail(open ? memoId : null);
  const memo = detail.data?.memo ?? null;
  // 下書きのキーにログイン中のユーザーを使うので、それが分かってからフォームを作る
  const meReady = !useMe().isPending;
  const invalidate = useInvalidate();
  // ピンのメモを開いたら既読にする(未読・更新の印を消す)。開くたびに 1 回だけ
  const unread = memo !== null && memo.readState !== null && memo.readState !== "READ";
  const unreadId = unread ? memo.id : null;
  useEffect(() => {
    if (!open || unreadId === null) return;
    void markMemoRead(unreadId).then(() => invalidate.memos());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- invalidate は毎回作られるが中身は同じ
  }, [open, unreadId]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[96vh] w-[96vw] max-w-[96vw] overflow-y-auto sm:max-w-[1600px]"
        // 開いたとき、Radix の標準ではタイトル欄にフォーカスが行く。本文に置きたいので、ここでは
        // ダイアログ本体に止める(エディタが出たら、本文にカーソルを置く)
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          (e.target as HTMLElement).focus();
        }}
      >
        {memoId !== null && detail.isPending && (
          <DialogHeader>
            <DialogTitle>メモ</DialogTitle>
            <DialogDescription>読み込み中…</DialogDescription>
          </DialogHeader>
        )}
        {memoId !== null && detail.isError && (
          <DialogHeader>
            <DialogTitle>メモ</DialogTitle>
            <DialogDescription role="alert" className="text-destructive">
              メモを開けませんでした。削除されたか、見られなくなった可能性があります。
            </DialogDescription>
          </DialogHeader>
        )}
        {meReady && (memoId === null || memo) && (
          // memo が変わったら(別のメモを開いたとき)フォームを作り直す
          <MemoForm
            key={memo?.id ?? draftId ?? "new"}
            memo={memo}
            projectId={memo?.projectId ?? projectId}
            draftId={memo ? draftIdOfMemo(memo.id) : draftId}
            onDone={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function MemoForm({
  memo,
  projectId,
  draftId,
  onDone,
}: {
  memo: MemoView | null;
  projectId: string | null;
  draftId: string | null;
  onDone: () => void;
}) {
  const isProject = projectId !== null;
  const readOnlyForMentions = memo !== null && !memo.canEdit;
  // 「@」の補完の候補(編集できるときだけ取る)
  const mentions = useMentionItems(projectId, !readOnlyForMentions);
  const invalidate = useInvalidate();
  const email = useMe().data?.user.email ?? null;
  const draftsOn = email !== null && draftId !== null;
  const readOnly = memo !== null && !memo.canEdit;
  const saved = { title: memo?.title ?? "", body: memo?.body ?? "", icon: memo?.icon ?? "" };
  // このフォームはメモを読み込んだあとに作る(メモごとに key で作り直す)ので、初期値は defaultValues でよい。
  // values にすると、再取得のたびに入力中の内容がサーバーの値で上書きされる
  // 新しいメモで、一覧の「下書き」から再開したときは、その下書きの内容から始める(本人が選んだ復元)
  const [resumed] = useState<MemoDraft | null>(() =>
    memo === null && draftsOn ? readDraft(email, draftId) : null,
  );
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: resumed
      ? { title: resumed.title, body: resumed.body, icon: resumed.icon ?? "" }
      : saved,
  });

  // ---- 下書き: 自動で保存し、復元は手動
  // 既存のメモは、開いた時点で保存済みの内容と違う下書きがあれば「復元 / 破棄」を選んでもらう。
  // 新しいメモの下書きは、一覧から選んで再開するので、ここでは確認しない。
  const [pending, setPending] = useState<MemoDraft | null>(() => {
    if (!draftsOn || readOnly || memo === null) return null;
    const draft = readDraft(email, draftId);
    if (
      draft &&
      (draft.title !== saved.title ||
        draft.body !== saved.body ||
        (draft.icon ?? "") !== saved.icon)
    )
      return draft;
    if (draft) clearDraft(email, draftId);
    return null;
  });
  const values = useWatch({ control: form.control });
  const finished = useRef(false); // 保存・削除・破棄のあとは下書きを書き戻さない
  const latest = useRef({ values, pending });
  useEffect(() => {
    latest.current = { values, pending };
  });
  const persist = () => {
    const { values: v, pending: p } = latest.current;
    // 選択待ちの下書きがある間は、上書きしない
    if (!draftsOn || readOnly || finished.current || p) return;
    const title = v.title ?? "";
    const body = v.body ?? "";
    const icon = v.icon ?? "";
    if (title === saved.title && body === saved.body && icon === saved.icon)
      clearDraft(email, draftId);
    else
      writeDraft(email, draftId, {
        title,
        body,
        icon,
        baseRevision: memo?.revision ?? null,
        savedAt: Date.now(),
      });
  };
  useEffect(() => {
    const t = setTimeout(persist, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- persist は最新の値を ref から読む
  }, [values.title, values.body, values.icon, pending]);
  // 入力して 500ms 以内に閉じても、最後の入力を失わない
  useEffect(() => persist, []); // eslint-disable-line react-hooks/exhaustive-deps

  const restore = () => {
    if (!pending) return;
    form.setValue("title", pending.title, { shouldDirty: true });
    form.setValue("body", pending.body, { shouldDirty: true });
    form.setValue("icon", pending.icon ?? "", { shouldDirty: true });
    setPending(null);
  };
  const discard = () => {
    if (draftsOn) clearDraft(email, draftId);
    setPending(null);
  };
  const finish = () => {
    finished.current = true;
    if (draftsOn) clearDraft(email, draftId);
    onDone();
  };

  const save = useMutation({
    mutationFn: (v: FormValues) =>
      memo
        ? updateMemo(memo.id, { ...v, revision: pinRevision })
        : createMemo(isProject ? { ...v, projectId } : v),
    onSuccess: () => {
      // 閉じるのを、一覧などの取り直しの完了まで待たせない
      void invalidate.memos();
      finish();
    },
  });
  const remove = useMutation({
    mutationFn: () => deleteMemo((memo as MemoView).id),
    onSuccess: () => {
      // 先に閉じてから、消えたメモの詳細をキャッシュから外す(開いたままだと、取り直しに行ってしまう)
      finish();
      void invalidate.memoDeleted((memo as MemoView).id);
    },
  });
  // ピン留めは保存とは別に、その場で反映する(題名・本文の編集中でも、入力は消さない)
  const [pinnedAt, setPinnedAt] = useState(memo?.pinnedAt ?? null);
  const [pinRevision, setPinRevision] = useState(memo?.revision ?? 0);
  const pin = useMutation({
    mutationFn: (pinned: boolean) =>
      updateMemoPinned((memo as MemoView).id, { pinned, revision: pinRevision }),
    onSuccess: ({ memo: m }) => {
      setPinnedAt(m.pinnedAt);
      setPinRevision(m.revision);
      void invalidate.memos();
    },
  });
  const [confirming, setConfirming] = useState(false);
  // 共有されたメモの所有者は名前で出す(ユーザーマスタ。名前が無ければメール)
  const nameOf = useUserName(memo !== null && memo.role !== "OWNER");
  const pinButton = memo?.canPin && (
    <Button
      type="button"
      variant="outline"
      className="shrink-0"
      disabled={pin.isPending}
      onClick={() => pin.mutate(pinnedAt === null)}
    >
      {pinnedAt === null ? <Pin /> : <PinOff />}
      {pinnedAt === null ? "ボードに留める" : "ボードから外す"}
    </Button>
  );

  return (
    <form
      noValidate
      onSubmit={form.handleSubmit((v) => save.mutate(v))}
      className="flex flex-col gap-4"
    >
      <DialogHeader>
        <div className="flex items-center gap-1.5">
          <DialogTitle>
            {memo === null ? "メモを追加" : readOnly ? "メモ" : "メモを編集"}
          </DialogTitle>
          {/* 説明は画面に書かず、見出しの右のヒントにする(ユーザー要望 2026-10-15) */}
          {memo === null && (
            <Hint label="メモを追加の説明">
              {isProject ? NEW_PROJECT_MEMO_HINT : NEW_MEMO_HINT}
            </Hint>
          )}
        </div>
        <DialogDescription asChild>
          {/* バッジのある・ないで高さが変わらないよう、段の高さを揃える */}
          <div className="flex min-h-6 flex-wrap items-center gap-2">
            {memo === null && (
              <span className="sr-only">{isProject ? NEW_PROJECT_MEMO_HINT : NEW_MEMO_HINT}</span>
            )}
            {memo === null && resumed && (
              <span>
                下書きから再開しています({when.format(new Date(resumed.savedAt))} に自動保存)。
              </span>
            )}
            {memo && isProject && (
              <>
                <Badge variant="secondary">プロジェクトのメモ</Badge>
                {memo.role !== "OWNER" && (
                  <Badge variant="info">{memo.canEdit ? "編集可" : "閲覧のみ"}</Badge>
                )}
                {pinnedAt && <Badge variant="info">ボードに留めています</Badge>}
                <span>
                  作った人:{" "}
                  {memo.role === "OWNER" ? "自分" : (nameOf(memo.ownerEmail) ?? memo.ownerEmail)}
                </span>
              </>
            )}
            {memo && !isProject && memo.role !== "OWNER" && (
              <>
                <Badge variant="secondary">共有されたメモ</Badge>
                <Badge variant="info">{MEMO_ACCESS_LABELS[memo.role]}</Badge>
                <span>所有者: {nameOf(memo.ownerEmail) ?? memo.ownerEmail}</span>
              </>
            )}
            {memo?.role === "OWNER" && !isProject && (
              <span>{memo.shared ? "共有中のメモです。" : "自分だけに見えるメモです。"}</span>
            )}
          </div>
        </DialogDescription>
      </DialogHeader>

      {pending && (
        <div
          role="status"
          className="flex flex-col gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <p className="font-medium">
              保存していない下書きがあります({when.format(new Date(pending.savedAt))} に自動保存)
            </p>
            {memo && pending.baseRevision !== memo.revision && (
              <p className="text-xs">
                下書きを書いたあとに、このメモは更新されています。復元すると、その更新を上書きします。
              </p>
            )}
          </div>
          <div className="flex shrink-0 gap-2">
            <Button type="button" size="sm" onClick={restore}>
              下書きを復元
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={discard}>
              破棄
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="memo-title">タイトル</Label>
        {readOnly ? (
          // 編集できるときの題名の欄と同じ高さにする
          <p className="flex min-h-9 items-center gap-2 text-lg font-medium">
            <MemoIcon value={memo?.icon ?? ""} className="size-5 text-[20px]" />
            {memo?.title || "無題のメモ"}
          </p>
        ) : (
          <div className="flex gap-2">
            {/* アイコンは題名の左。押すと選ぶ一覧が開く(Notion と同じ。2026-10-15) */}
            <MemoIconPicker
              value={values.icon ?? ""}
              onChange={(v) => form.setValue("icon", v, { shouldDirty: true })}
            />
            <Input
              id="memo-title"
              placeholder="無題のメモ"
              {...form.register("title")}
              // Tab・Enter で本文へ移る(Enter で保存しない。日本語の変換の確定では移らない。2026-10-15)
              onKeyDown={(e) => {
                if (e.nativeEvent.isComposing || e.keyCode === 229) return;
                if ((e.key === "Enter" || e.key === "Tab") && !e.shiftKey) {
                  e.preventDefault();
                  focusBody(e.currentTarget.form);
                }
              }}
            />
            {/* 共有はタイトルの横に置く。共有できるのは所有者だけで、新規は保存するまで押せない */}
            {memo === null && !isProject && (
              <Button
                type="button"
                variant="outline"
                disabled
                title="保存すると共有できます"
                className="shrink-0"
              >
                <Share2 />
                共有
              </Button>
            )}
            {pinButton}
            {memo?.canManage && !isProject && (
              <MemoShareDialog
                memo={memo}
                trigger={
                  <Button type="button" variant="outline" className="shrink-0">
                    <Share2 />
                    共有
                  </Button>
                }
              />
            )}
          </div>
        )}
        {form.formState.errors.title && (
          <p className="text-xs text-destructive">{form.formState.errors.title.message}</p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>本文</Label>
        <Suspense
          fallback={
            // 読み込み中も同じ高さの枠を出して、エディタが現れたときに画面が跳ねないようにする
            <div
              className="flex items-center justify-center rounded-md border text-sm text-muted-foreground"
              style={{ height: EDITOR_HEIGHT }}
            >
              エディタを読み込み中…
            </div>
          }
        >
          {readOnly ? (
            // 閲覧のみでも、編集できるときと同じ高さの枠に出す(開くメモで大きさが変わらないように。
            // ユーザー要望 2026-10-15)。長ければ枠の中でスクロールする
            <div
              data-testid="memo-body-view"
              className="overflow-y-auto rounded-md border px-4 py-3"
              style={{ height: EDITOR_HEIGHT }}
            >
              <LazyMarkdownView source={memo?.body ?? ""} />
            </div>
          ) : (
            <Controller
              control={form.control}
              name="body"
              render={({ field }) => (
                <LazyMarkdownEditor
                  value={field.value}
                  onChange={field.onChange}
                  height={EDITOR_HEIGHT}
                  autoFocus
                  mentions={mentions}
                />
              )}
            />
          )}
        </Suspense>
        {form.formState.errors.body && (
          <p className="text-xs text-destructive">{form.formState.errors.body.message}</p>
        )}
      </div>

      <InlineError error={save.error ?? remove.error ?? pin.error} />
      <DialogFooter className="items-center sm:justify-between">
        <div className="flex gap-2">
          {memo?.canManage && (
            <>
              <Dialog open={confirming} onOpenChange={setConfirming}>
                <DialogTrigger asChild>
                  <Button type="button" variant="outline">
                    <Trash2 />
                    削除
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>このメモを削除しますか?</DialogTitle>
                    <DialogDescription>
                      {isProject
                        ? "プロジェクトの全員から見えなくなります。元に戻せません。"
                        : "共有している人からも見えなくなります。元に戻せません。"}
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setConfirming(false)}>
                      キャンセル
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate()}
                    >
                      削除する
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
        {readOnly ? (
          // 保存の代わりに閉じるを置き、下の段の高さも編集できるときと揃える
          <Button type="button" variant="outline" onClick={onDone}>
            閉じる
          </Button>
        ) : (
          <Button type="submit" disabled={save.isPending}>
            <Save />
            保存
          </Button>
        )}
      </DialogFooter>
    </form>
  );
}
